import { useTranslation } from 'react-i18next'
import { Globe } from 'lucide-react'
import { applyLanguage, LANGUAGES, type LangCode } from '../../i18n/index'
import { cn } from '../../lib/utils'

type Props = {
  /** Show the label next to the icon. Defaults to true. */
  showLabel?: boolean
  /** Show the short language code ("EN"/"AR") instead of the full label. */
  code?: boolean
  /** Icon/label scale. Defaults to 'sm'. */
  size?: 'sm' | 'lg'
  /**
   * 'pill' (default) = filled globe + label. 'icon' = a bare, muted ghost globe
   * button matching the top-bar / sidebar language icon used across the apps.
   */
  variant?: 'pill' | 'icon'
  className?: string
}

export function LanguageToggle({ showLabel = true, code = false, size = 'sm', variant = 'pill', className }: Props) {
  const { i18n } = useTranslation()
  const current = (i18n.language as LangCode) === 'ar' ? 'ar' : 'en'
  const next = LANGUAGES.find(l => l.code !== current)!
  const big = size === 'lg'
  const label = code ? next.code.toUpperCase() : next.label

  const toggle = () => {
    i18n.changeLanguage(next.code)
    applyLanguage(next.code as LangCode)
  }

  // Plain icon button — identical to the in-app language switcher (Sidebar).
  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={`Switch language to ${next.label}`}
        className={cn(
          'flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors',
          className,
        )}
      >
        <Globe className="w-4 h-4" />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Switch language to ${next.label}`}
      className={cn(
        'flex items-center gap-2 p-1.5 rounded-xl hover:bg-muted transition-colors',
        className,
      )}
    >
      <div
        className={cn(
          'rounded-lg flex items-center justify-center shrink-0',
          big ? 'w-10 h-10' : 'w-7 h-7',
        )}
      >
        <Globe
          className={cn('fill-white stroke-black', big ? 'w-6 h-6' : 'w-4 h-4')}
          strokeWidth={2}
        />
      </div>
      {showLabel && (
        <span className={cn('font-bold leading-none px-1', big ? 'text-base' : 'text-[11px]')}>
          {label}
        </span>
      )}
    </button>
  )
}
